import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useLanguage } from '../../Context/LanguageContext';
import ReactMarkdown from 'react-markdown';
import html2pdf from 'html2pdf.js'; // <-- ADD THIS IMPORT
import './ChatWidget.css';

const VERCEL_CHAT_API = process.env.REACT_APP_CHAT_API_URL || '/api/chat';

function ChatWidget() {
  const { language } = useLanguage();
  
  // --- UPDATED: Visual Viewport integration with requestAnimationFrame for smooth 60fps syncing ---
  useEffect(() => {
    if (!window.visualViewport) return;

    let rafId;

    const updateViewport = () => {
      // Cancel any pending frames to prevent stacking
      if (rafId) cancelAnimationFrame(rafId);
      
      // requestAnimationFrame perfectly syncs the CSS variable update with the phone's native display refresh rate
      rafId = requestAnimationFrame(() => {
        document.documentElement.style.setProperty('--vv-height', `${window.visualViewport.height}px`);
        document.documentElement.style.setProperty('--vv-bottom', `${window.innerHeight - window.visualViewport.height - window.visualViewport.offsetTop}px`);
      });
    };

    window.visualViewport.addEventListener("resize", updateViewport);
    window.visualViewport.addEventListener("scroll", updateViewport);
    updateViewport(); // Initial call

    return () => {
      window.visualViewport.removeEventListener("resize", updateViewport);
      window.visualViewport.removeEventListener("scroll", updateViewport);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);
  // ------------------------------------------------------------------------

  const ROTATING_TEXTS = useMemo(() => {
    return language === "ko-KR"
      ? ["회사 소개?", "서비스?", "채용 정보?", "제안 내용?"]
      : ["Our Company?", "Our Services?", "Our Jobs?", "Our Offerings?"];
  }, [language]);

  const [messages, setMessages] = useState(() => {
    try {
      const saved = sessionStorage.getItem(`jk_sel_chat_messages_${language}`);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [typingMessageId, setTypingMessageId] = useState(null);
const typingIntervalRef = useRef(null);
  
  const [isChatOpen, setIsChatOpen] = useState(() => {
    try { return sessionStorage.getItem('jk_sel_chat_open') === 'true'; } 
    catch { return false; }
  });

  const [isMinimized, setIsMinimized] = useState(false);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  
  const [showPromo, setShowPromo] = useState(false);
  const [isPromoFading, setIsPromoFading] = useState(false); 
  const [promoClosed, setPromoClosed] = useState({ en: false, ko: false });
  
  const [errorStatus, setErrorStatus] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showEndChatConfirm, setShowEndChatConfirm] = useState(false);

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0, startPosX: 0, startPosY: 0 });

  const typingRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const messagesContainerRef = useRef(null);
const justOpenedRef = useRef(false);
const lastAssistantMessageRef = useRef(null);
const scrollPositionRef = useRef(0);
const restoringFromMinimizeRef = useRef(false);
  const currentLang = language === "ko-KR" ? "ko" : "en";

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(`jk_sel_chat_messages_${language}`);
      setMessages(saved ? JSON.parse(saved) : []);
    } catch {
      setMessages([]);
    }
  }, [language]);

  useEffect(() => {
    setCurrentTextIndex(0);
    setDisplayText('');
    setIsDeleting(false);
    setShowPromo(false);
    setIsPromoFading(false);
    setIsMinimized(false);
    setPosition({ x: 0, y: 0 });
  }, [language]);

  useEffect(() => {
    if (promoClosed[currentLang] || isChatOpen) return;

    let showTimer, fadeTimer, removeTimer;

    showTimer = setTimeout(() => {
      setShowPromo(true);
      setIsPromoFading(false);

      fadeTimer = setTimeout(() => {
        setIsPromoFading(true);
        removeTimer = setTimeout(() => {
          setShowPromo(false);
          setPromoClosed(prev => ({ ...prev, [currentLang]: true }));
        }, 400); 
      }, 5000);
      
    }, 2000);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(fadeTimer);
      clearTimeout(removeTimer);
    };
  }, [currentLang, promoClosed, isChatOpen]);

  // Scroll Logic
  useEffect(() => {
    if (!isChatOpen || isMinimized) return;

    const container = messagesContainerRef.current;
    if (!container) return;

    // Restoring after maximize
    if (restoringFromMinimizeRef.current) {
      requestAnimationFrame(() => {
        container.scrollTop = scrollPositionRef.current;
        restoringFromMinimizeRef.current = false;
      });
      return;
    }

    requestAnimationFrame(() => {
      // Opening chat
      if (justOpenedRef.current) {
        container.style.scrollBehavior = "auto";
        container.scrollTop = container.scrollHeight;
        container.style.scrollBehavior = "smooth";
        justOpenedRef.current = false;
        return;
      }

      // 1. If waiting for the backend OR currently animating the typing effect:
      // Auto-scroll to the very bottom so the user can follow along as text appears
      if (isLoading || typingMessageId !== null) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: "smooth"
        });
        return;
      }

      // 2. If the backend is done AND the typing animation has completely finished:
      // Scroll back up to the top (start) of the AI's response
      if (
        !isLoading && 
        typingMessageId === null && 
        messages.length > 0 &&
        messages[messages.length - 1].role === "assistant" &&
        lastAssistantMessageRef.current
      ) {
        lastAssistantMessageRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
      }
    });

  // FIX: Added `typingMessageId` to the dependency array so the effect knows when typing starts/stops
  }, [messages, isLoading, isChatOpen, isMinimized, typingMessageId]);

  // --- UPDATED: Focus Logic Fix ---
  useEffect(() => {
    // Bulletproof check to ensure focus NEVER fires on touch/mobile devices
    const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (window.innerWidth <= 768);
    
    if (!isChatOpen || isLoading || isMinimized || isTouchDevice) return;

    inputRef.current?.focus();
  }, [isChatOpen, isLoading, messages, isMinimized]);
  // --------------------------------

  useEffect(() => {
    sessionStorage.setItem('jk_sel_chat_open', isChatOpen);
  }, [isChatOpen]);

  useEffect(() => {
  try {
    sessionStorage.setItem(
      `jk_sel_chat_messages_${language}`,
      JSON.stringify(messages)
    );
  } catch (e) {
    console.error(e);
  }
}, [messages, language]);

  useEffect(() => {
    const currentWord = ROTATING_TEXTS[currentTextIndex];
    let timeout;

    if (!isDeleting) {
      timeout = setTimeout(() => {
        setDisplayText(currentWord.substring(0, displayText.length + 1));
        if (displayText === currentWord) {
          setTimeout(() => setIsDeleting(true), 1500);
        }
      }, 100);
    } else {
      timeout = setTimeout(() => {
        setDisplayText(currentWord.substring(0, displayText.length - 1));
        if (displayText === '') {
          setIsDeleting(false);
          setCurrentTextIndex(prev => (prev + 1) % ROTATING_TEXTS.length);
        }
      }, 50);
    }

    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, currentTextIndex, ROTATING_TEXTS]);

  const handleMouseDown = (e) => {
    const isHeader = e.target.closest('.chatbot-header');
    const isInputArea = e.target.closest('.chatbot-input-area');

    if (!isHeader && !isInputArea) return;

    const tag = e.target.tagName.toLowerCase();
    
    if (tag === 'input' || tag === 'button' || tag === 'a' || e.target.closest('button')) return;
    
    e.preventDefault(); 

    setIsDragging(true);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPosX: position.x,
      startPosY: position.y
    };
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      setPosition({
        x: dragRef.current.startPosX + dx,
        y: dragRef.current.startPosY + dy
      });
    };

    const handleMouseUp = () => {
      if (isDragging) setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);
const handleDownloadTranscript = async () => {
    setIsMenuOpen(false);
    if (messages.length === 0) return;

    const now = new Date();
    const pad = (num) => String(num).padStart(2, '0');
    const dateStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    const timeStr = `${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
    
    // FIX: Dynamically translate the filename to Conversation
    const isKo = language === "ko-KR";
    const baseFileName = isKo ? "JK_SEL_채팅_대화" : "JK_SEL_Chat_Conversation";
    const dynamicFilename = `${baseFileName} (${dateStr}_${timeStr}).pdf`;

    // FIX: Dynamically translate the header text to Conversation
    const headerTitle = isKo ? "AI 어시스턴트 채팅 대화" : "AI Assistant Chat Conversation";
    const generatedPrefix = isKo ? "생성일:" : "Generated on:";

    // 1. Build the HTML string directly 
    let chatHtml = `
      <div style="width: 100%; max-width: 800px; box-sizing: border-box; padding: 20px; font-family: 'Noto Sans KR', 'Malgun Gothic', 'Apple SD Gothic Neo', 'Nanum Gothic', sans-serif; color: #333; background: white;">
        <div style="border-bottom: 2px solid #eee; padding-bottom: 15px; margin-bottom: 25px;">
          <h1 style="color: #111; font-size: 24px; margin: 0 0 5px 0;">${headerTitle}</h1>
          <p style="color: #777; font-size: 12px; margin: 0;">${generatedPrefix} ${now.toLocaleString()}</p>
        </div>
    `;

    messages.forEach((msg) => {
      const roleColor = msg.role === 'user' ? '#3498DB' : '#111';
      const roleName = msg.role === 'user' ? (isKo ? '나' : 'You') : (isKo ? '에이전트' : 'Agent');
      const time = new Date(msg.timestamp).toLocaleString();
      
      let contentHtml = msg.content
         .replace(/</g, '&lt;') 
         .replace(/>/g, '&gt;')
         .replace(/\n\n/g, '</p><p style="margin: 0 0 10px 0;">')
         .replace(/\n/g, '<br>')
         .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
         .replace(/\*(.*?)\*/g, '<em>$1</em>')
         .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" style="color: #3498DB; text-decoration: none; word-break: break-all;">$1</a>');

      contentHtml = contentHtml.replace(/(?:^|<br>)\s*[-*]\s+(.*?)(?=(?:<br>|$))/g, '<br>&bull; $1');
      if (contentHtml.startsWith('<br>')) contentHtml = contentHtml.substring(4);
      contentHtml = `<p style="margin: 0 0 10px 0;">${contentHtml}</p>`;

      const isAssistant = msg.role === 'assistant';
      const borderStyles = isAssistant ? 'padding-bottom:12px; border-bottom:1px solid #e5e5e5;' : '';

      // FIX: Changed 'avoid' to 'auto' to prevent massive gaps and allow natural continuity across pages
      chatHtml += `
        <div style="margin-bottom: 14px; ${borderStyles} page-break-inside: auto; break-inside: auto;">
          <div style="color: ${roleColor}; font-weight: bold; font-size: 14px; margin-bottom: 5px;">${roleName} at ${time}</div>
          <div style="font-size: 14px; white-space: pre-wrap; word-wrap: break-word;">${contentHtml}</div>
        </div>
      `;
    });

    chatHtml += `</div>`;

    // 2. Configure PDF Options
    const opt = {
      margin:       15, 
      filename:     dynamicFilename, 
      image:        { type: 'jpeg', quality: 0.98 },
      html2canvas:  { scale: 2, useCORS: true, windowWidth: 800 }, 
      jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' },
      // FIX: Removed 'avoid-all' from pagebreak to allow seamless wrapping
      pagebreak:    { mode: ['css', 'legacy'] } 
    };

    // 3. Generate and download the PDF
    try {
      await html2pdf().set(opt).from(chatHtml).save();
    } catch (error) {
      console.error("PDF generation failed:", error);
    }
  };
  const confirmEndChat = () => {
    setMessages([]);
    sessionStorage.removeItem(`jk_sel_chat_messages_${language}`);
    setShowEndChatConfirm(false);
    setIsChatOpen(false);
    setPosition({ x: 0, y: 0 }); 
  };

  async function handleSendMessage() {
    if (!inputValue.trim()) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: inputValue,
      timestamp: new Date(),
      source: 'user'
    };

    setMessages(prev => [...prev, userMessage]);
    const userQuery = inputValue;
    setInputValue('');
    setIsLoading(true);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      const recentHistory = messages
  .slice(-6)
  .map(msg => ({
    role: msg.role,
    content: msg.content
  }));

      const response = await fetch(VERCEL_CHAT_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'omit',
       body: JSON.stringify({
  userMessage: userQuery,
  chatHistory: recentHistory
}),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();

     if (data.response && data.response.trim().length > 0) {

    const assistantId = Date.now() + 1;

    setTypingMessageId(assistantId);

    setMessages(prev => [
        ...prev,
        {
            id: assistantId,
            role: "assistant",
            content: "",
            timestamp: new Date(),
            source: data.source || "groq",
            chunks_used: data.chunks_used,
            detected_intent: data.detected_intent
        }
    ]);

    const words = data.response.split(" ");

    let index = 0;

    typingIntervalRef.current = setInterval(() => {

        index++;

        setMessages(prev =>
            prev.map(msg => {

                if (msg.id !== assistantId)
                    return msg;

                return {
                    ...msg,
                    content: words.slice(0, index).join(" ")
                };

            })
        );

        if (index >= words.length) {

            clearInterval(typingIntervalRef.current);

            typingIntervalRef.current = null;

            setTypingMessageId(null);

        }

    }, 35);

    setErrorStatus(null);

} else {
        throw new Error('Empty response from backend');
      }

    } catch (error) {
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: 'assistant',
        content: language === "ko-KR"
          ? "연결에 문제가 있습니다. 다시 시도하시거나 info@jksel.com 으로 문의해 주세요."
          : "I'm having trouble connecting. Please try again or contact us at info@jksel.com",
        timestamp: new Date(),
        source: 'error'
      }]);
      setErrorStatus('error');
    } finally {
      setIsLoading(false);
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const closePromoWithAnimation = () => {
    setIsPromoFading(true);
    setTimeout(() => {
      setShowPromo(false);
      setPromoClosed(prev => ({ ...prev, [currentLang]: true }));
    }, 400);
  };
  useEffect(() => {

    return () => {

        if (typingIntervalRef.current) {

            clearInterval(typingIntervalRef.current);

        }

    };

}, []);

  return (
    <div className="chatbot-container">
      {!isChatOpen && (
        <>
          {showPromo && (
            <div className={`chatbot-promo ${isPromoFading ? 'fade-out' : ''}`}>
              <button
                className="promo-close"
                onClick={closePromoWithAnimation}
              >✕</button>
              <div className="promo-text">
                {language === "ko-KR" ? "JK SEL에 대해 무엇이든 물어보세요!" : "Ask me anything about JK SEL!"}
              </div>
            </div>
          )}

          <button
            className="chatbot-bubble"
            onClick={() => {
              justOpenedRef.current = true;
         setIsChatOpen(true);

setTimeout(() => {
    if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop =
            messagesContainerRef.current.scrollHeight;
    }
}, 0);
              setIsMinimized(false);
              setPosition({ x: 0, y: 0 });
              setShowPromo(false);
              setPromoClosed(prev => ({ ...prev, [currentLang]: true }));
            }}
          >
            <span className="chatbot-bubble-icon">💬</span>
            <span className="chatbot-bubble-text">
              {language === "ko-KR" ? "AI 어시스턴트" : "AI Assistant"}
            </span>
          </button>
        </>
      )}

      {isChatOpen && (
        <div
          className={`chatbot-window ${!isMinimized ? "open" : "minimized"}`}
          style={
            window.innerWidth > 768 
              ? {
                  right: `${30 - position.x}px`,
                  bottom: `${30 - position.y}px`
                }
              : undefined // Let CSS strictly handle mobile layout
          }
          onMouseDown={window.innerWidth > 768 ? handleMouseDown : undefined}
        >
          
          <div className="chatbot-header">
            <div className="chatbot-header-info">
              <span>{language === "ko-KR" ? "AI 어시스턴트" : "AI Assistant"}</span>
            </div>
            
            <div className="chatbot-header-actions">
              <div className="options-menu-wrapper" ref={menuRef}>
                <button
                  className="chatbot-header-btn"
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  title="Options"
                  disabled={isMinimized}
                  style={{
                    opacity: isMinimized ? 0.3 : undefined,
                    cursor: isMinimized ? 'not-allowed' : 'pointer'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="1"></circle>
                    <circle cx="19" cy="12" r="1"></circle>
                    <circle cx="5" cy="12" r="1"></circle>
                  </svg>
                </button>
                
                {isMenuOpen && !isMinimized && (
                  <div className="options-dropdown">
                    <button 
                      onClick={handleDownloadTranscript} 
                      disabled={messages.length === 0}
                    >
                      {/* FIX: Updated UI text to Download Conversation */}
                      {language === "ko-KR" ? "대화 다운로드" : "Download Conversation"}
                    </button>
                    <button 
                      onClick={() => { setIsMenuOpen(false); setShowEndChatConfirm(true); }} 
                      disabled={messages.length === 0}
                    >
                      {language === "ko-KR" ? "채팅 종료" : "End chat"}
                    </button>
                  </div>
                )}
              </div>

              <button
  className="chatbot-header-btn"
  onClick={() => {

    if (!isMinimized) {
  // Minimize
  if (messagesContainerRef.current) {
    scrollPositionRef.current =
      messagesContainerRef.current.scrollTop;
  }

} else {
  // Maximize
  restoringFromMinimizeRef.current = true;
}

setIsMinimized(!isMinimized);
setIsMenuOpen(false);

  }}
>
                {isMinimized ? "▲" : "▼"}
              </button>
              <button className="chatbot-header-btn" onClick={() => { 
                  setIsChatOpen(false); 
                  setIsMinimized(false); 
                  setPosition({ x: 0, y: 0 }); 
                }}>
                ✕
              </button>
            </div>
          </div>

          <div className={`chatbot-content ${isMinimized ? "hidden" : ""}`}>
            
            {showEndChatConfirm && (
              <div className="chatbot-modal-overlay">
                <div className="chatbot-modal">
                  <p>
                    {language === "ko-KR" 
                      ? "이 채팅을 종료하시겠습니까?" 
                      : "Are you sure you want to end this chat conversation?"}
                  </p>
                  <div className="chatbot-modal-buttons">
                    <button className="btn-no" onClick={() => setShowEndChatConfirm(false)}>
                      {language === "ko-KR" ? "아니요" : "No"}
                    </button>
                    <button className="btn-yes" onClick={confirmEndChat}>
                      {language === "ko-KR" ? "네" : "Yes"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {errorStatus && (
              <div className={`status-alert ${errorStatus}`}>
                {errorStatus === 'offline' && "📚 Knowledge base not loaded - basic mode"}
                {errorStatus === 'degraded' && "⚠️ Using local information (AI service unavailable)"}
                {errorStatus === 'no_match' && "🔍 No matching information found"}
              </div>
            )}

            <div
  className="chatbot-messages"
  ref={messagesContainerRef}
>
              {messages.length === 0 && (
                <div className="chatbot-welcome">
                  <div className="welcome-hello">{language === "ko-KR" ? "안녕하세요!" : "Hello!"}</div>
                  <div className="welcome-title">{language === "ko-KR" ? "알고 싶으신가요" : "Want to know about"}</div>
                  <div className="welcome-animated">
                    {displayText}
                    <span className="typing-cursor">|</span>
                  </div>
                </div>
              )}

              {messages.map((msg, index) => (
                <div
                  key={msg.id}
                  ref={
                    msg.role === "assistant" &&
                    index === messages.length - 1
                      ? lastAssistantMessageRef
                      : null
                  }
                  className={`chatbot-msg-row ${msg.role}`}
                >
                  <div className="chatbot-msg-bubble">
                    {msg.role === 'assistant' ? (
                      <ReactMarkdown
                        components={{
                          a: ({ node, children, href, ...props }) => {
                            const isInternal = href && (href.startsWith('/') || href.includes('jkselab.com'));
                            const isMail = href && href.startsWith('mailto:');

                            const handleLinkClick = (e) => {
                              if (isInternal) {
                                e.preventDefault(); 
                                
                                let relativePath = href;
                                if (href.includes('jkselab.com')) {
                                  const parts = href.split('jkselab.com');
                                  relativePath = parts[1] || '/';
                                  if (!relativePath.startsWith('/')) relativePath = '/' + relativePath;
                                }
                                
                                setIsChatOpen(false);
                                setIsMinimized(false);
                                window.location.assign(relativePath);
                              }
                            };

                            return (
                              <a 
                                {...props} 
                                href={href}
                                onClick={handleLinkClick}
                                target={!isInternal && !isMail ? "_blank" : "_self"} 
                                rel={!isInternal && !isMail ? "noopener noreferrer" : undefined}
                              >
                                {children}
                              </a>
                            );
                          }
                        }}
                      >
                        {/* FIX: Auto-linkify raw text web links and emails before rendering */}
                        {(msg.content + (typingMessageId === msg.id ? "▋" : ""))
                          // FIX: Updated regex to properly allow dots, slashes, and hyphens inside the URL
                          .replace(/(?<!\]\()(?<!\()(https?:\/\/[\w\-./]+[\w\-/])/g, '[$1]($1)')
                          .replace(/(?<!\]\()(?<!\()([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g, '[$1](mailto:$1)')
                        }
                      </ReactMarkdown>
                    ) : (
                      msg.content
                    )}
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="chatbot-msg-row assistant" ref={typingRef}>
                  <div className="chatbot-typing">
                    {language === "ko-KR" ? "생각 중..." : "Thinking..."}
                  </div>
                </div>
              )}
            </div>

            <div className="chatbot-input-area">
              <input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={language === "ko-KR" ? "JK SEL에 대해 무엇이든 물어보세요..." : "Ask me anything about JK SEL..."}
                disabled={isLoading}
              />
              <button onClick={handleSendMessage} disabled={isLoading || !inputValue.trim()}>
                {isLoading ? '...' : '→'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatWidget;